import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Users, Handshake, ShoppingBag, Newspaper, CheckCircle, ChevronRight, User, Phone, Mail, MapPin, Loader2 } from "lucide-react";
import axios from "axios";
import { useLanguage } from "../contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATES = ["Madhya Pradesh","Maharashtra","Uttar Pradesh","Gujarat","Rajasthan","Delhi","Karnataka","Tamil Nadu","West Bengal","Punjab","Bihar","Andhra Pradesh","Telangana","Kerala","Odisha","Haryana","Jharkhand","Chhattisgarh","Uttarakhand","Himachal Pradesh","Assam","Other"];

const SKILLS = ["Medical / Healthcare", "Translation / Language", "Logistics / Transport", "IT / Technology", "Security / Crowd Management", "First Aid", "Communication / PR", "Photography / Videography", "Teaching / Education", "Other"];

// InputField component defined OUTSIDE to prevent re-creation on every render
const InputField = ({ label, icon: Icon, ...props }) => (
  <div>
    <label className="text-white/50 text-xs uppercase tracking-widest font-body block mb-1.5">{label}</label>
    <div className="relative">
      {Icon && <Icon size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30" />}
      <input {...props} className={`w-full ${Icon ? "pl-10" : "pl-4"} pr-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-saffron text-white placeholder:text-white/25 text-sm font-body outline-none transition-all`} />
    </div>
  </div>
);

export default function Register() {
  const { t } = useLanguage();
  const [step, setStep] = useState(1); // 1: category, 2: form, 3: success
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [registrationId, setRegistrationId] = useState("");
  const [form, setForm] = useState({
    name: "", email: "", phone: "", state: "", pilgrim_count: 1,
    special_needs: "", organization: "", skills: "", message: "",
  });

  const CATEGORIES = [
    { key: "pilgrim", label: t.reg.pilgrim, icon: Users, desc: t.reg.pilgrimDesc, color: "#FF9933" },
    { key: "volunteer", label: t.reg.volunteer, icon: Handshake, desc: t.reg.volunteerDesc, color: "#FFD700" },
    { key: "vendor", label: t.reg.vendor, icon: ShoppingBag, desc: t.reg.vendorDesc, color: "#FF5500" },
    { key: "media", label: t.reg.media, icon: Newspaper, desc: t.reg.mediaDesc, color: "#FF9933" },
  ];

  const update = (field, val) => setForm(f => ({ ...f, [field]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError(t.reg.nameRequired); return; }
    if (!form.phone.trim()) { setError(t.reg.phoneRequired); return; }
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/registrations`, { ...form, category });
      setRegistrationId(res.data.id);
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.detail || t.reg.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-900 pt-28 pb-20" data-testid="register-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-6">
          <p className="text-saffron text-sm uppercase tracking-widest font-body mb-3">{t.reg.badge}</p>
          <h1 className="font-heading font-bold text-4xl md:text-5xl text-white mb-4">{t.reg.title}</h1>
          <p className="font-body text-white/50">{t.reg.subtitle}</p>
        </motion.div>

        {/* Registration Purpose Notice */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ delay: 0.2 }}
          className="bg-amber-900/20 border border-amber-700/30 rounded-xl p-4 mb-8"
        >
          <p className="text-amber-200/80 text-sm font-body text-center leading-relaxed">
            <span className="font-semibold text-amber-400">{t.reg.volunteerNotice}</span><br/>
            <span className="text-amber-200/60 text-xs">{t.reg.volunteerNoticeDetail}</span>
          </p>
        </motion.div>

        {/* Step indicator */}
        {step < 3 && (
          <div className="flex items-center justify-center gap-3 mb-10">
            {[1, 2].map(s => (
              <div key={s} className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${step >= s ? "bg-saffron text-stone-900" : "bg-white/10 text-white/30"}`}>
                  {s}
                </div>
                <span className={`text-xs font-body ${step >= s ? "text-white/60" : "text-white/20"}`}>
                  {s === 1 ? t.reg.step1 : t.reg.step2}
                </span>
                {s < 2 && <ChevronRight size={14} className="text-white/20" />}
              </div>
            ))}
          </div>
        )}

        <AnimatePresence mode="wait">
          {/* Step 1: Category Selection */}
          {step === 1 && (
            <motion.div key="step1" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {CATEGORIES.map(({ key, label, icon: Icon, desc, color }) => (
                  <motion.button key={key} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                    onClick={() => { setCategory(key); setStep(2); }}
                    data-testid={`category-select-${key}`}
                    className={`glass-card rounded-2xl p-6 text-left hover:border-saffron/30 transition-all group ${category === key ? "border-saffron/30 bg-saffron/5" : ""}`}>
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
                      style={{ background: `rgba(255,153,51,0.12)` }}>
                      <Icon size={22} style={{ color }} />
                    </div>
                    <h3 className="font-heading font-semibold text-lg text-white mb-1">{label}</h3>
                    <p className="font-body text-white/40 text-sm">{desc}</p>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 2: Form */}
          {step === 2 && (
            <motion.div key="step2" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}>
              <div className="glass-card rounded-3xl p-8">
                <div className="flex items-center gap-3 mb-7">
                  {(() => {
                    const cat = CATEGORIES.find(c => c.key === category);
                    const Icon = cat?.icon;
                    return (
                      <>
                        <div className="w-10 h-10 rounded-xl bg-saffron/10 flex items-center justify-center">
                          {Icon && <Icon size={20} className="text-saffron" />}
                        </div>
                        <div>
                          <p className="font-heading font-semibold text-white">{cat?.label} Registration</p>
                          <button onClick={() => setStep(1)} className="text-white/40 text-xs hover:text-saffron transition-colors font-body">Change category</button>
                        </div>
                      </>
                    );
                  })()}
                </div>

                <form onSubmit={handleSubmit} className="space-y-5" data-testid="registration-form">
                  <InputField label="Full Name *" icon={User} type="text" placeholder="Your full name"
                    value={form.name} onChange={e => update("name", e.target.value)} required data-testid="reg-name" />
                  <InputField label="Phone Number *" icon={Phone} type="tel" placeholder="+91 XXXXX XXXXX"
                    value={form.phone} onChange={e => update("phone", e.target.value)} required data-testid="reg-phone" />
                  <InputField label="Email Address" icon={Mail} type="email" placeholder="your@email.com"
                    value={form.email} onChange={e => update("email", e.target.value)} data-testid="reg-email" />

                  <div>
                    <label className="text-white/50 text-xs uppercase tracking-widest font-body block mb-1.5">
                      <MapPin size={11} className="inline mr-1" /> State
                    </label>
                    <select value={form.state} onChange={e => update("state", e.target.value)}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-saffron text-white text-sm font-body outline-none transition-all"
                      data-testid="reg-state">
                      <option value="" className="bg-stone-800">Select your state</option>
                      {STATES.map(s => <option key={s} value={s} className="bg-stone-800">{s}</option>)}
                    </select>
                  </div>

                  {category === "pilgrim" && (
                    <div>
                      <label className="text-white/50 text-xs uppercase tracking-widest font-body block mb-1.5">Number of Pilgrims</label>
                      <input type="number" min="1" max="50" value={form.pilgrim_count}
                        onChange={e => update("pilgrim_count", parseInt(e.target.value) || 1)}
                        className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-saffron text-white text-sm font-body outline-none transition-all"
                        data-testid="reg-pilgrim-count" />
                    </div>
                  )}

                  {category === "pilgrim" && (
                    <InputField label="Special Needs / Accessibility" type="text" placeholder="Any special requirements..."
                      value={form.special_needs} onChange={e => update("special_needs", e.target.value)} data-testid="reg-special-needs" />
                  )}

                  {category === "volunteer" && (
                    <div>
                      <label className="text-white/50 text-xs uppercase tracking-widest font-body block mb-1.5">Skills / Area of Service</label>
                      <select value={form.skills} onChange={e => update("skills", e.target.value)}
                        className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-saffron text-white text-sm font-body outline-none transition-all"
                        data-testid="reg-skills">
                        <option value="" className="bg-stone-800">Select your skill</option>
                        {SKILLS.map(s => <option key={s} value={s} className="bg-stone-800">{s}</option>)}
                      </select>
                    </div>
                  )}

                  {(category === "vendor" || category === "media") && (
                    <InputField label={category === "vendor" ? "Business / Organization Name" : "Media Organization"}
                      icon={Newspaper} type="text" placeholder="Organization name"
                      value={form.organization} onChange={e => update("organization", e.target.value)} data-testid="reg-organization" />
                  )}

                  <div>
                    <label className="text-white/50 text-xs uppercase tracking-widest font-body block mb-1.5">Message (Optional)</label>
                    <textarea rows={3} placeholder="Any additional information..."
                      value={form.message} onChange={e => update("message", e.target.value)}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-saffron text-white placeholder:text-white/25 text-sm font-body outline-none transition-all resize-none"
                      data-testid="reg-message" />
                  </div>

                  {error && (
                    <div className="px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm font-body" data-testid="reg-error">
                      {error}
                    </div>
                  )}

                  <button type="submit" disabled={loading} data-testid="reg-submit-btn"
                    className="w-full py-4 rounded-xl bg-saffron text-stone-900 font-body font-bold text-base hover:shadow-[0_0_25px_rgba(255,153,51,0.4)] transition-all active:scale-98 disabled:opacity-60 flex items-center justify-center gap-2">
                    {loading ? <><Loader2 size={18} className="animate-spin" /> Registering...</> : "Complete Registration"}
                  </button>
                </form>
              </div>
            </motion.div>
          )}

          {/* Step 3: Success */}
          {step === 3 && (
            <motion.div key="step3" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
              className="glass-card rounded-3xl p-10 text-center" style={{ border: "1px solid rgba(255,153,51,0.2)" }}
              data-testid="registration-success">
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: "spring" }}
                className="w-20 h-20 rounded-full bg-saffron/10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle size={40} className="text-saffron" />
              </motion.div>
              <h2 className="font-heading font-bold text-3xl text-white mb-3">Jai Mahakal!</h2>
              <p className="font-body text-white/60 text-base mb-2">You are registered for Ujjain Maha Kumbh 2028</p>
              <p className="font-body text-white/40 text-sm mb-6">Registration ID:</p>
              <div className="inline-block px-5 py-2 rounded-xl bg-saffron/10 border border-saffron/20 font-body text-saffron text-sm mb-8 break-all" data-testid="registration-id">
                {registrationId}
              </div>
              <p className="font-body text-white/40 text-sm mb-8">
                Category: <span className="text-white capitalize">{category}</span> &nbsp;|&nbsp; Name: <span className="text-white">{form.name}</span>
              </p>
              <button onClick={() => { setStep(1); setCategory(""); setForm({ name:"",email:"",phone:"",state:"",pilgrim_count:1,special_needs:"",organization:"",skills:"",message:"" }); setRegistrationId(""); }}
                className="px-8 py-3 rounded-full bg-saffron text-stone-900 font-body font-semibold hover:shadow-[0_0_15px_rgba(255,153,51,0.4)] transition-all"
                data-testid="register-another-btn">
                Register Another
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
